'use client';

import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { Icon } from '../../icons';
import { Caption } from '../Caption/Caption';
import { AnimatePresence, motion } from 'motion/react';
import { InputText } from '../InputText/InputText';
// Reuse InputSelect styles for consistent look-and-feel
import '../InputSelect/InputSelect.css';

export interface MultiSelectOption<TValue> {
  value: TValue;
  label: string;
}

export interface InputMultiSelectProps<TValue> {
  label: string;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  caption?: React.ReactNode;
  searchable?: boolean; // Enables a client-side search field inside the dropdown
  options: MultiSelectOption<TValue>[];
  values: TValue[];
  onChange: (values: TValue[]) => void;
  leadingVisual?: React.ReactNode;
  trailingVisual?: React.ReactNode;
  message?: string;
  messageType?: '' | 'error' | 'success';
  id?: string;
}

export const InputMultiSelect = React.memo(function InputMultiSelect<TValue>({
  label = 'Label',
  required = false,
  disabled = false,
  placeholder = '',
  caption,
  searchable = false,
  options,
  values,
  onChange,
  leadingVisual,
  trailingVisual,
  message,
  messageType = '',
  id,
}: InputMultiSelectProps<TValue>) {
  const reactId = useId();
  const selectId = id ?? `input-multi-select-${reactId}`;
  const captionId = caption ? `${selectId}-caption` : undefined;
  const messageId = message ? `${selectId}-message` : undefined;
  const [open, setOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [lastSelectedSValue, setLastSelectedSValue] = useState<string | null>(null);

  const items = useMemo(() => options.map((o) => ({ ...o, sValue: String(o.value) })), [options]);
  const selectedStringValues = useMemo(() => new Set(values.map((v) => String(v))), [values]);
  const isSearchEnabled = searchable || items.length > 6;
  const normalizedQuery = searchQuery.trim().toLowerCase();
  const filteredItems = useMemo(() => {
    if (!isSearchEnabled || normalizedQuery.length === 0) return items;
    return items.filter(
      (o) =>
        o.label.toLowerCase().includes(normalizedQuery) ||
        o.sValue.toLowerCase().includes(normalizedQuery),
    );
  }, [isSearchEnabled, items, normalizedQuery]);

  const handleToggleValue = (sValue: string) => {
    const isAlreadySelected = selectedStringValues.has(sValue);
    if (!isAlreadySelected) {
      setLastSelectedSValue(sValue);
    } else {
      setLastSelectedSValue(null);
    }
    const nextValues = isAlreadySelected
      ? values.filter((v) => String(v) !== sValue)
      : [...values, items.find((i) => i.sValue === sValue)?.value as TValue];
    onChange(nextValues);
  };

  const openDropdown = useCallback(() => {
    if (disabled) return;
    setOpen(true);
    setLastSelectedSValue(null);
    // If any selected exists, highlight the first occurrence in the filtered list
    const firstSelectedIndex = filteredItems.findIndex((i) => selectedStringValues.has(i.sValue));
    setHighlightedIndex(firstSelectedIndex >= 0 ? firstSelectedIndex : -1);
  }, [disabled, filteredItems, selectedStringValues]);

  const closeDropdown = useCallback(() => {
    setOpen(false);
    setHighlightedIndex(-1);
    setSearchQuery('');
  }, []);

  // Close on outside click and Escape
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(target) &&
        triggerRef.current &&
        !triggerRef.current.contains(target)
      ) {
        closeDropdown();
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeDropdown();
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, closeDropdown]);

  const onTriggerKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (disabled) return;
    if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (!open) {
        openDropdown();
      } else {
        setHighlightedIndex((prev) => Math.min(prev + 1, filteredItems.length - 1));
      }
    } else if (e.key === 'ArrowUp' && open) {
      e.preventDefault();
      setHighlightedIndex((prev) => Math.max(prev - 1, 0));
    }
  };

  const onListKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex((prev) => Math.min(prev + 1, filteredItems.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      const opt = filteredItems[highlightedIndex];
      if (opt) {
        handleToggleValue(opt.sValue);
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      closeDropdown();
    }
  };

  // Keep highlighted index within bounds when filter changes
  useEffect(() => {
    if (highlightedIndex >= filteredItems.length) {
      setHighlightedIndex(filteredItems.length - 1);
    }
  }, [filteredItems.length, highlightedIndex]);

  // Auto-focus search input when opening and search is enabled
  useEffect(() => {
    if (open && isSearchEnabled) {
      const id = window.setTimeout(() => {
        searchInputRef.current?.focus();
      }, 0);
      return () => window.clearTimeout(id);
    }
  }, [open, isSearchEnabled]);

  const selectedCount = values.length;
  const triggerLabel = useMemo(() => {
    if (selectedCount === 0) return placeholder || 'Select...';
    if (selectedCount === 1) {
      const only = items.find((i) => selectedStringValues.has(i.sValue));
      return only ? only.label : '1 selected';
    }
    return `${selectedCount} selected`;
  }, [selectedCount, placeholder, items, selectedStringValues]);

  return (
    <div className="input-select" data-disabled={disabled}>
      <div>
        <span className="input-select__label" id={`${selectId}-label`}>
          {label}
          {required && <span className="input-select__required">*</span>}
        </span>
        {caption && (
          <Caption>
            <span id={captionId}>{caption}</span>
          </Caption>
        )}
      </div>

      <div className="input-select__dropdown" ref={dropdownRef}>
        <motion.button
          ref={triggerRef}
          type="button"
          className="input-select__trigger-button"
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-labelledby={`${selectId}-label`}
          aria-describedby={[captionId, messageId].filter(Boolean).join(' ') || undefined}
          aria-invalid={messageType === 'error' || undefined}
          onClick={() => (open ? closeDropdown() : openDropdown())}
          onKeyDown={onTriggerKeyDown}
          data-disabled={disabled}
          disabled={disabled}
        >
          {leadingVisual}
          <span className="input-select__trigger-text">
            <AnimatePresence initial={false} mode="wait">
              <motion.span
                key={selectedCount}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.14 }}
                className="input-select__trigger-text-inner"
              >
                {triggerLabel}
              </motion.span>
            </AnimatePresence>
          </span>
          {trailingVisual}
          <Icon name="ChevronDown" className="input-select__arrow" />
        </motion.button>

        <AnimatePresence>
          {open && (
            <motion.div
              role="listbox"
              aria-multiselectable="true"
              id={`${selectId}-listbox`}
              aria-labelledby={`${selectId}-label`}
              className="input-select__content"
              initial={{ opacity: 0, scale: 0.98, transformOrigin: 'top left' }}
              animate={{ opacity: 1, scale: 1, transformOrigin: 'top left' }}
              exit={{ opacity: 0, scale: 0.98, transformOrigin: 'top left' }}
              transition={{ duration: 0.14 }}
              style={{ willChange: 'transform, opacity' }}
              onKeyDown={onListKeyDown}
              tabIndex={-1}
            >
              {isSearchEnabled && (
                <div className="input-select__search-wrapper">
                  <InputText
                    ref={searchInputRef}
                    value={searchQuery}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      setSearchQuery(String(e.target.value));
                      setHighlightedIndex(-1);
                    }}
                    placeholder="Search..."
                    leadingVisual="Search"
                  />
                </div>
              )}

              <div
                className="input-select__list"
                data-search-enabled={isSearchEnabled || undefined}
              >
                {filteredItems.length === 0 && (
                  <div className="input-select__no-results" aria-live="polite">
                    No results
                  </div>
                )}

                {filteredItems.map((opt, index) => {
                  const isSelected = selectedStringValues.has(opt.sValue);
                  const isHighlighted = highlightedIndex === index;
                  return (
                    <motion.div
                      key={opt.sValue}
                      role="option"
                      aria-selected={isSelected}
                      className="input-select__item"
                      data-highlighted={isHighlighted || undefined}
                      tabIndex={-1}
                      whileTap={{ scale: 0.98 }}
                      initial={false}
                      animate={
                        isSelected && lastSelectedSValue === opt.sValue
                          ? { scale: [1, 1.04, 1] }
                          : { scale: 1 }
                      }
                      transition={{ duration: 0.2, times: [0, 0.5, 1], ease: [0.22, 1, 0.36, 1] }}
                      onMouseEnter={() => setHighlightedIndex(index)}
                      onClick={() => {
                        handleToggleValue(opt.sValue);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleToggleValue(opt.sValue);
                        }
                      }}
                    >
                      <span className="input-select__item-label">{opt.label}</span>
                      <AnimatePresence initial={false}>
                        {isSelected && (
                          <motion.span
                            className="input-select__item-check"
                            initial={{ opacity: 0, scale: 0.85 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.85 }}
                            transition={{ duration: 0.12 }}
                          >
                            <Icon name="Check" />
                          </motion.span>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {message && (
        <span id={messageId} className="input-select__message" data-type={messageType}>
          {message}
        </span>
      )}
    </div>
  );
});

InputMultiSelect.displayName = 'InputMultiSelect';
